"use client";

import { useRef, useState } from "react";

import { TextLink } from "@/components/optilase/text-link";
import { Button, ButtonLink } from "@/components/ui/button";
import { useTriggerAction } from "@/lib/action-link";
import { type Surface } from "@/components/optilase/surface";
import { ANALYTICS_EVENTS, track } from "@/lib/analytics";
import { cn } from "@/lib/utils";

export type TreatmentPrice = {
  name: string;
  priceFrom: string;
  priceUnit?: string;
  monthlyFrom?: string;
  priceNote?: string;
  description: string;
  href: string;
  linkLabel?: string;
};

export type PricingViewProps = {
  heading: string;
  intro: string;
  suitabilityLabel?: string;
  suitabilityHref?: string;
  suitabilityAction?: string | null;
  treatments: TreatmentPrice[];
  surface?: Surface | null;
};

const THEME = {
  dark: {
    section: "bg-navy-03",
    heading: "text-white",
    intro: "text-white",
    pillActive: "bg-white font-semibold text-navy-03",
    pillIdle: "border border-white font-medium text-white hover:bg-white/10",
    cardTitle: "text-grey-09",
  },
  light: {
    section: "bg-teal-02",
    heading: "text-navy-02",
    intro: "text-grey-07",
    pillActive: "bg-teal-07 font-semibold text-white",
    pillIdle: "border border-teal-04 font-medium text-teal-08 hover:bg-teal-03/40",
    cardTitle: "text-grey-08",
  },
} as const;

export default function PricingView({
  heading,
  intro,
  suitabilityLabel = "Am I suitable?",
  suitabilityHref,
  suitabilityAction,
  treatments,
  surface,
}: PricingViewProps) {
  const theme = surface && !surface.onDark ? "light" : "dark";
  const trigger = useTriggerAction();
  const action = suitabilityAction || (suitabilityHref ? null : "open_quiz");
  const buttonClass = cn(
    "h-12 w-full text-lg md:w-fit",
    theme === "light" ? "lg:h-14 lg:text-button" : "lg:hidden",
  );
  const [active, setActive] = useState(0);
  const current = treatments[active];
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const touchStart = useRef<{ x: number; y: number } | null>(null);

  const select = (i: number, location: string) => {
    tabRefs.current[i]?.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
    if (i === active) return;
    setActive(i);
    track(ANALYTICS_EVENTS.PROCEDURE_SELECTED, { treatment: treatments[i].name, location });
  };

  const onTouchStart = (e: React.TouchEvent) => {
    const t = e.touches[0];
    touchStart.current = { x: t.clientX, y: t.clientY };
  };

  const onTouchEnd = (e: React.TouchEvent) => {
    const start = touchStart.current;
    touchStart.current = null;
    if (!start || treatments.length < 2) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
    const step = dx < 0 ? 1 : -1;
    select((active + step + treatments.length) % treatments.length, "pricing-swipe");
  };
  const t = THEME[theme];

  return (
    <section
      style={surface ? { backgroundColor: surface.backgroundColor } : undefined}
      className={cn(
        surface ? "" : t.section,
        "overflow-x-clip px-5 py-10 md:px-10 md:py-12 lg:px-20 lg:py-20",
      )}
    >
      <div className="mx-auto grid max-w-7xl gap-6 max-md:max-w-[520px] md:grid-cols-2 md:gap-x-8 md:gap-y-6 lg:grid-cols-[445px_minmax(0,1fr)] lg:gap-x-16 lg:gap-y-8">
        <div className="flex flex-col gap-4 md:col-start-1 md:row-start-1 lg:max-w-97.25 lg:gap-5">
          <h2 className={cn("text-section", t.heading)}>{heading}</h2>
          <p className={cn("text-base leading-relaxed tracking-[-0.01em] lg:text-lg", t.intro)}>
            {intro}
          </p>
        </div>

        <div className="flex min-w-0 flex-col gap-6 md:col-start-2 md:row-span-2 md:row-start-1 lg:gap-8">
          <ul className="-mx-5 flex [scrollbar-width:none] gap-2 overflow-x-auto px-5 pb-1 md:-mr-10 md:ml-0 md:pr-10 md:pl-0 lg:mx-0 lg:flex-wrap lg:gap-x-2 lg:gap-y-3 lg:overflow-visible lg:px-0 lg:pb-0 [&::-webkit-scrollbar]:hidden">
            {treatments.map((treatment, i) => (
              <li key={treatment.name} className="shrink-0">
                <button
                  ref={(el) => {
                    tabRefs.current[i] = el;
                  }}
                  type="button"
                  onClick={() => select(i, "pricing-switcher")}
                  aria-pressed={i === active}
                  className={cn(
                    "h-11 shrink-0 cursor-pointer rounded-full px-6 text-sm whitespace-nowrap transition-colors",
                    i === active ? t.pillActive : t.pillIdle,
                  )}
                >
                  {treatment.name}
                </button>
              </li>
            ))}
          </ul>

          {current ? (
            <div
              className="relative mr-[26px] touch-pan-y lg:mr-0"
              onTouchStart={onTouchStart}
              onTouchEnd={onTouchEnd}
            >
              <div className="relative grid max-w-130.75 [--card-shrink:0.045] [--card-step:6.33px] lg:[--card-shrink:0.02] lg:[--card-step:24px]">
                {treatments.map((treatment, i) => {
                  const rank = (i - active + treatments.length) % treatments.length;
                  const front = rank === 0;
                  return (
                    <article
                      key={treatment.name}
                      inert={!front}
                      aria-hidden={!front}
                      style={{
                        gridArea: "1 / 1",
                        zIndex: treatments.length - rank,
                        transform: front
                          ? undefined
                          : `translateX(calc(${rank} * var(--card-step))) scaleY(calc(1 - ${rank} * var(--card-shrink)))`,
                      }}
                      className={cn(
                        "flex flex-col rounded-xl py-6 pr-6 pl-5 shadow-[0_6px_4px_rgba(0,0,0,0.2)] transition-[transform,background-color] duration-500 ease-[cubic-bezier(0.32,0.72,0,1)] lg:pr-15 lg:pl-6",
                        front ? "bg-white" : "bg-teal-01",
                      )}
                    >
                      <h3 className={cn("text-h5", t.cardTitle)}>{treatment.name}</h3>

                      <div className="mt-4">
                        {/\d/.test(treatment.priceFrom ?? "") ? (
                          <p className="text-grey-06 text-sm">From </p>
                        ) : null}
                        <p className="flex items-end gap-2">
                          <span className="text-teal-07 text-[32px] leading-none font-bold">
                            {treatment.priceFrom}
                          </span>{" "}
                          {treatment.priceUnit ? (
                            <span className="text-grey-06 text-sm">{treatment.priceUnit}</span>
                          ) : null}
                        </p>{" "}
                        {treatment.monthlyFrom ? (
                          <p className="text-grey-06 mt-4 text-base font-bold">
                            {treatment.monthlyFrom}
                          </p>
                        ) : null}{" "}
                        {treatment.priceNote ? (
                          <p className="text-grey-06 mt-1 text-sm">{treatment.priceNote}</p>
                        ) : null}
                      </div>

                      <p className="text-grey-06 mt-4 text-base leading-relaxed tracking-[-0.01em]">
                        {treatment.description}
                      </p>

                      {treatment.href && treatment.href !== "#" ? (
                        <TextLink
                          href={treatment.href}
                          className="text-teal-08 mt-auto pt-9 whitespace-normal"
                        >
                          {treatment.linkLabel || "View treatment"}
                        </TextLink>
                      ) : null}
                    </article>
                  );
                })}
              </div>
            </div>
          ) : null}
        </div>

        <div className="md:col-start-1 md:row-start-2 md:self-start">
          {action ? (
            <Button
              variant={theme === "light" ? "navy" : "teal"}
              onClick={(e) => trigger(action, e)}
              className={cn(buttonClass, "cursor-pointer")}
            >
              {suitabilityLabel}
            </Button>
          ) : (
            <ButtonLink
              href={suitabilityHref ?? "#"}
              variant={theme === "light" ? "navy" : "teal"}
              className={buttonClass}
            >
              {suitabilityLabel}
            </ButtonLink>
          )}

          {theme === "dark" ? (
            <TextLink
              href={action ? undefined : (suitabilityHref ?? "#")}
              onClick={action ? (e) => trigger(action, e) : undefined}
              className="text-teal-05 hidden lg:inline-flex"
            >
              {suitabilityLabel}
            </TextLink>
          ) : null}
        </div>
      </div>
    </section>
  );
}
